import { z, type ZodType } from '@customerio/hydra-validation';
import type { Delivery as UIAPIDelivery, DeliveryDetail as UIAPIDeliveryDetail } from '@customerio/hydra-ui-api';
import { defineEntity } from '../shared/define-entity';

/**
 * Delivery entity — the Outbox delivery log. Reused by campaign / broadcast /
 * newsletter / transactional / person delivery lists.
 *
 * The wire model carries every state timestamp as a nullable unix-second int
 * (`sent`, `delivered`, `drafted`, …) plus a server `state` enum. We derive
 * the display state from the timestamps (see `utils.ts`) to match the Ember
 * model exactly — the server `state` enum is NOT equivalent (it lacks
 * `holdout`/`canceled`, has `dropped`). Timestamps are left as raw numbers.
 *
 * `pendingSend` / `pendingRetry` / `pendingDelete` are client-only optimistic
 * flags (not on the wire) — the Ember model's `@tracked` flags, folded onto
 * the record. Bulk-op mutations set them in place instead of refetching.
 */
export type Delivery = UIAPIDelivery & {
  pendingSend?: boolean;
  pendingRetry?: boolean;
  pendingDelete?: boolean;
};

export type DeliveryDetail = UIAPIDeliveryDetail;

// oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
const DeliveryFromApi = z.looseObject({
  id: z.string(),
  action_type: z.string(),
}) as unknown as ZodType<Delivery, unknown>;

/** @public */
export const delivery = defineEntity<Delivery, Delivery, DeliveriesListParams, undefined, 'workspace'>({
  name: 'delivery',
  scope: 'workspace',
  pk: (d) => d.id,
  schema: { read: DeliveryFromApi },
});

/**
 * The list filter params (mirrors the Ember `getDeliveryQuery` output + the
 * generated list query params). `continuation` is intentionally excluded from
 * the query key — the infinite query pages on it, so it must not fragment the
 * list slot.
 */
export interface DeliveriesListParams {
  recipient?: string;
  internal_id?: string;
  drafts?: boolean;
  metric?: string;
  start_ts?: number;
  end_ts?: number;
  type?: string;
  action_id?: string;
  campaign_id?: number;
  trigger_id?: string;
  template_id?: number;
  newsletter_id?: number;
  transactional_message_id?: number;
  customer_id?: string;
}

export const deliveryKeys = {
  ...delivery.keys,
  filters: (workspaceId: number) => [...delivery.keys.all(workspaceId), 'filters'] as const,
  detailContent: (workspaceId: number, id: string) => [...delivery.keys.detail(workspaceId, id), 'content'] as const,
  detailEdit: (workspaceId: number, id: string) => [...delivery.keys.detail(workspaceId, id), 'edit'] as const,
};

export function parseDelivery(raw: UIAPIDelivery): Delivery {
  return delivery.parse(raw);
}
